import { useState } from 'react'
import { router } from '@inertiajs/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import logoImage from '/assets/logo-transparent.png'

interface Site {
  id: number
  name: string
}

interface QuestionResponse {
  id: number
  response_text: string
}

interface Question {
  id: number
  question_text: string
  question_type: string
  required: boolean
  responses: QuestionResponse[]
}

interface RegisterProps {
  sites: Site[]
  questionnaire_sections: Record<string, Question[]>
  errors?: Record<string, string[]>
}

const registrationSchema = z.object({
  site_id: z.string().min(1, 'Please select a clinic'),
  legal_name: z.string().min(1, 'Legal name is required'),
  alias_name: z.string().optional(),
  phone_number: z.string().optional().or(z.literal('')),
  email: z.string().email('Valid email is required').optional().or(z.literal('')),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  date_of_birth: z.string().min(1, 'Date of birth is required'),
  zip_code: z.string().regex(/^\d{5}(-\d{4})?$/, 'Must be a valid ZIP code'),
  emergency_contact_1_name: z.string().optional(),
  emergency_contact_1_phone: z.string().optional(),
  emergency_contact_2_name: z.string().optional(),
  emergency_contact_2_phone: z.string().optional(),
  emergency_contact_3_name: z.string().optional(),
  emergency_contact_3_phone: z.string().optional(),
  consent_sms: z.boolean().refine((val) => val === true, 'You must consent to SMS communication'),
  consent_data_share: z.boolean().refine((val) => val === true, 'You must consent to data sharing'),
  consent_non_emergency_notice: z.boolean().refine((val) => val === true, 'You must consent to non-emergency notices'),
  consent_hipaa_ack: z.boolean().refine((val) => val === true, 'You must acknowledge HIPAA notice'),
})

type RegistrationFormData = z.infer<typeof registrationSchema>

export default function Register({ sites, questionnaire_sections, errors: serverErrors }: RegisterProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [questionnaireResponses, setQuestionnaireResponses] = useState<Record<string, string>>({})
  const [error, setError] = useState('')

  const { register, handleSubmit, formState: { errors: formErrors, isSubmitting }, watch, setValue } = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      consent_sms: false,
      consent_data_share: false,
      consent_non_emergency_notice: false,
      consent_hipaa_ack: false,
    }
  })

  // Calculate total steps: basic info (1) + emergency contacts (1) + questionnaire sections (N) + consents (1)
  const sectionNames = Object.keys(questionnaire_sections)
  const totalSteps = 3 + sectionNames.length

  const onSubmit = async (formData: RegistrationFormData) => {
    setError('')

    // Validate questionnaire responses
    const allSections = Object.values(questionnaire_sections).flat()
    const requiredQuestions = allSections.filter(q => q.required)
    const missingResponses = requiredQuestions.filter(q => !questionnaireResponses[q.id.toString()])

    if (missingResponses.length > 0) {
      setError('Please answer all required questions in the questionnaire')
      setCurrentStep(3)
      return
    }

    try {
      router.post('/register', {
        patient: {
          ...formData,
          site_id: parseInt(formData.site_id),
          consent_signed_at: new Date().toISOString(),
        },
        questionnaire_responses: questionnaireResponses,
      })
    } catch (err) {
      setError('An error occurred during registration. Please try again.')
    }
  }

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleQuestionnaireChange = (questionId: number, responseValue: string | number) => {
    setQuestionnaireResponses({
      ...questionnaireResponses,
      [questionId.toString()]: responseValue.toString(),
    })
  }

  // Helper function to get the current questionnaire section index
  const getCurrentSectionIndex = () => {
    // Step 1: Basic Info
    // Step 2: Emergency Contacts
    // Steps 3 to (3 + sectionNames.length - 1): Questionnaire sections
    // Last step: Consents
    if (currentStep <= 2) return -1
    if (currentStep > 2 + sectionNames.length) return -1
    return currentStep - 3
  }

  const currentSectionIndex = getCurrentSectionIndex()
  const isQuestionnaireStep = currentSectionIndex >= 0 && currentSectionIndex < sectionNames.length
  const currentSectionName = isQuestionnaireStep ? sectionNames[currentSectionIndex] : null
  const currentSectionQuestions = currentSectionName ? questionnaire_sections[currentSectionName] : []

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <img
              src={logoImage}
              alt="Breakthrough Health"
              className="h-16 w-auto"
            />
          </div>
          <CardTitle className="text-2xl">Patient Registration</CardTitle>
          <CardDescription>
            Step {currentStep} of {totalSteps}
          </CardDescription>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {serverErrors && Object.keys(serverErrors).length > 0 && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                {Object.entries(serverErrors).map(([field, messages]) => (
                  <div key={field}>{messages.join(', ')}</div>
                ))}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold mb-4">Basic Information</h3>

                <div className="space-y-2">
                  <Label htmlFor="site_id">Select Clinic *</Label>
                  <Select onValueChange={(value) => setValue('site_id', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a clinic" />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map((site) => (
                        <SelectItem key={site.id} value={site.id.toString()}>
                          {site.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formErrors.site_id && (
                    <p className="text-sm text-red-500">{formErrors.site_id.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="legal_name">Legal Name *</Label>
                    <Input id="legal_name" {...register('legal_name')} />
                    {formErrors.legal_name && (
                      <p className="text-sm text-red-500">{formErrors.legal_name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="alias_name">Preferred Name (Optional)</Label>
                    <Input id="alias_name" {...register('alias_name')} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone_number">Phone Number</Label>
                    <Input id="phone_number" type="tel" placeholder="+15551234567" {...register('phone_number')} />
                    {formErrors.phone_number && (
                      <p className="text-sm text-red-500">{formErrors.phone_number.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email (Optional)</Label>
                    <Input id="email" type="email" {...register('email')} />
                    {formErrors.email && (
                      <p className="text-sm text-red-500">{formErrors.email.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Date of Birth *</Label>
                    <Input id="date_of_birth" type="date" {...register('date_of_birth')} />
                    {formErrors.date_of_birth && (
                      <p className="text-sm text-red-500">{formErrors.date_of_birth.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="zip_code">ZIP Code *</Label>
                    <Input id="zip_code" placeholder="90210" {...register('zip_code')} />
                    {formErrors.zip_code && (
                      <p className="text-sm text-red-500">{formErrors.zip_code.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input id="password" type="password" {...register('password')} />
                  {formErrors.password && (
                    <p className="text-sm text-red-500">{formErrors.password.message}</p>
                  )}
                  <p className="text-sm text-muted-foreground">At least 8 characters</p>
                </div>
              </div>
            )}

            {/* Step 2: Emergency Contacts */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold mb-4">Emergency Contacts (Optional)</h3>

                <div className="space-y-4">
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-3">Contact 1</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="emergency_contact_1_name">Name</Label>
                        <Input id="emergency_contact_1_name" {...register('emergency_contact_1_name')} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emergency_contact_1_phone">Phone</Label>
                        <Input id="emergency_contact_1_phone" type="tel" {...register('emergency_contact_1_phone')} />
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-3">Contact 2</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="emergency_contact_2_name">Name</Label>
                        <Input id="emergency_contact_2_name" {...register('emergency_contact_2_name')} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emergency_contact_2_phone">Phone</Label>
                        <Input id="emergency_contact_2_phone" type="tel" {...register('emergency_contact_2_phone')} />
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-3">Contact 3</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="emergency_contact_3_name">Name</Label>
                        <Input id="emergency_contact_3_name" {...register('emergency_contact_3_name')} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emergency_contact_3_phone">Phone</Label>
                        <Input id="emergency_contact_3_phone" type="tel" {...register('emergency_contact_3_phone')} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Questionnaire Steps - One section per step */}
            {isQuestionnaireStep && currentSectionName && (
              <div className="space-y-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold">HCV Risk Assessment Questionnaire</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Section {currentSectionIndex + 1} of {sectionNames.length}: {currentSectionName}
                  </p>
                </div>

                <div className="border rounded-lg p-4">
                  <div className="space-y-4">
                    {currentSectionQuestions.map((question) => (
                      <div key={question.id} className="space-y-2">
                        <Label className="text-sm font-medium">
                          {question.question_text}
                          {question.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>

                        {question.question_type === 'single_choice' && (
                          <div className="space-y-2">
                            {question.responses.map((response) => (
                              <div key={response.id} className="flex items-center space-x-2">
                                <input
                                  type="radio"
                                  id={`q${question.id}_r${response.id}`}
                                  name={`question_${question.id}`}
                                  value={response.id.toString()}
                                  checked={questionnaireResponses[question.id.toString()] === response.id.toString()}
                                  onChange={() => handleQuestionnaireChange(question.id, response.id)}
                                  className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                />
                                <Label htmlFor={`q${question.id}_r${response.id}`} className="font-normal cursor-pointer">
                                  {response.response_text}
                                </Label>
                              </div>
                            ))}
                          </div>
                        )}

                        {question.question_type === 'text' && (
                          <Input
                            value={questionnaireResponses[question.id.toString()] || ''}
                            onChange={(e) => handleQuestionnaireChange(question.id, e.target.value)}
                            placeholder="Your answer"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Last Step: Consents */}
            {currentStep === totalSteps && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold mb-4">Required Consents</h3>

                <div className="space-y-4">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="consent_sms"
                      checked={watch('consent_sms')}
                      onCheckedChange={(checked) => setValue('consent_sms', checked as boolean)}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="consent_sms" className="cursor-pointer">
                        SMS Communication Consent *
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        I consent to receive text messages about my care, appointments, and test results.
                      </p>
                    </div>
                  </div>
                  {formErrors.consent_sms && (
                    <p className="text-sm text-red-500">{formErrors.consent_sms.message}</p>
                  )}

                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="consent_data_share"
                      checked={watch('consent_data_share')}
                      onCheckedChange={(checked) => setValue('consent_data_share', checked as boolean)}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="consent_data_share" className="cursor-pointer">
                        Data Sharing Consent *
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        I consent to sharing my health information with authorized healthcare providers for treatment purposes.
                      </p>
                    </div>
                  </div>
                  {formErrors.consent_data_share && (
                    <p className="text-sm text-red-500">{formErrors.consent_data_share.message}</p>
                  )}

                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="consent_non_emergency_notice"
                      checked={watch('consent_non_emergency_notice')}
                      onCheckedChange={(checked) => setValue('consent_non_emergency_notice', checked as boolean)}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="consent_non_emergency_notice" className="cursor-pointer">
                        Non-Emergency Communication Consent *
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        I understand that SMS and email are not secure for emergencies. For urgent matters, I will call 911.
                      </p>
                    </div>
                  </div>
                  {formErrors.consent_non_emergency_notice && (
                    <p className="text-sm text-red-500">{formErrors.consent_non_emergency_notice.message}</p>
                  )}

                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="consent_hipaa_ack"
                      checked={watch('consent_hipaa_ack')}
                      onCheckedChange={(checked) => setValue('consent_hipaa_ack', checked as boolean)}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="consent_hipaa_ack" className="cursor-pointer">
                        HIPAA Acknowledgment *
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        I acknowledge that I have received and reviewed the Notice of Privacy Practices.
                      </p>
                    </div>
                  </div>
                  {formErrors.consent_hipaa_ack && (
                    <p className="text-sm text-red-500">{formErrors.consent_hipaa_ack.message}</p>
                  )}
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6">
              {currentStep > 1 && (
                <Button type="button" variant="outline" onClick={prevStep}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
              )}

              {currentStep < totalSteps && (
                <Button type="button" onClick={nextStep} className="ml-auto">
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}

              {currentStep === totalSteps && (
                <Button type="submit" disabled={isSubmitting} className="ml-auto">
                  {isSubmitting ? 'Submitting...' : 'Complete Registration'}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
